// When IPC Global staff may ACT as a customer's admin (ENTRAID-307, Hugo Andrade 2026-10-25). Pure and shared: the
// backend enforces it (portal/backend/lib/staff-act.mjs, in front of every customer-admin route), the SPA only words it.
//
//   - On the highest tier (shared/tiers.mjs TOP_TIER) staff may act on the customer's behalf.
//   - On any other tier staff only SEE (the read-only operator view); the customer's own admins act.
//   - Exception, customer zero: a customer whose own Microsoft-verified domains include an IPC Global staff domain
//     (IPC Global is its own customer). Staff ARE that customer's users there, so they act like any customer admin.
//
// Fails closed: no record, an unset or unknown tier, or unreadable domains never allow.
import { TOP_TIER, tierLabel } from './tiers.mjs';
import { isStaffEmailDomain } from './email-address-policy.mjs';

// What a refused staff caller and the read-only banner say. The tier name comes from code, never a literal.
export const STAFF_ACT_MESSAGE = `IPC Global can act on your behalf only on the ${tierLabel(TOP_TIER)} plan. Ask your admin.`;

const MAX_DOMAINS = 1000;

/**
 * The customer's verified domains as lowercase names. Only the list Microsoft Graph verified for the tenant
 * (customer.watchVerifiedDomains.domains, refreshed by refresh-verified-domains): never the typed adminEmail or
 * primaryDomain, which a buyer or an operator can type.
 */
function verifiedDomainNames(customer) {
  const list = customer?.watchVerifiedDomains?.domains;
  if (!Array.isArray(list) || list.length > MAX_DOMAINS) return [];
  return list
    .map((e) => (typeof e === 'string' ? e : (e && typeof e === 'object' && typeof e.name === 'string' ? e.name : null)))
    .filter((d) => typeof d === 'string' && d.length > 0 && d.length <= 253)
    .map((d) => d.trim().toLowerCase());
}

/** True for customer zero: one of its Microsoft-verified domains is an IPC Global staff domain. */
// A subdomain counts too: Microsoft verifies a domain only through DNS, so verifying a subdomain needs DNS control of ipc-global.com.
export function isStaffOwnedCustomer(customer) {
  return verifiedDomainNames(customer).some((d) => isStaffEmailDomain(d));
}

/**
 * May IPC Global staff act as this customer's admin? `{ allowed, reason }`, reason one of 'top-tier', 'staff-owned',
 * 'tier' (refused: not the top tier) or 'no-record' (refused: nothing to decide on).
 */
export function staffMayActFor(customer) {
  if (!customer || typeof customer !== 'object') return { allowed: false, reason: 'no-record' };
  if (isStaffOwnedCustomer(customer)) return { allowed: true, reason: 'staff-owned' };
  if (customer.tierName === TOP_TIER) return { allowed: true, reason: 'top-tier' };
  return { allowed: false, reason: 'tier' };
}
